// Mirrors backend/app/schemas_outreach.py.
// Kept separate from types/index.ts to scope churn -- outreach types
// can move alongside outreach code without touching the dashboard core.

export interface OutreachSenderIn {
  email: string;
  display_name?: string | null;
  active?: boolean;
  default_cc?: string | null;
  default_bcc?: string | null;
}

export interface OutreachSenderUpdate {
  display_name?: string | null;
  active?: boolean;
  default_cc?: string | null;
  default_bcc?: string | null;
}

export interface OutreachSender {
  email: string;
  display_name: string | null;
  active: boolean;
  default_cc: string | null;
  default_bcc: string | null;
  graph_subscription_id: string | null;
  graph_subscription_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

// Maps event names ('step_1_sent', 'replied', 'rsvp_attending', etc.)
// to a status label or null (= no automatic status update on that event).
export type EventStatusMap = Record<string, string | null>;

export interface AffinityListConfigIn {
  affinity_list_id: number;
  label?: string | null;
  list_slug: string;
  status_field_id: number;
  status_dropdown_options?: Record<string, number>;
  event_to_status_map: EventStatusMap;
  terminal_status_values?: string[];
  paused_status_values?: string[];
  suppression_field_id?: number | null;
  suppression_dropdown_option_id?: number | null;
  suppression_reason_field_id?: number | null;
  dietary_field_id?: number | null;
  comments_field_id?: number | null;
  scope_field_id?: number | null;
  scope_field_name?: string | null;
  scope_dropdown_options?: Record<string, number>;
  response_url_template?: string | null;
}

export interface AffinityListConfigUpdate {
  label?: string | null;
  list_slug?: string;
  status_field_id?: number;
  status_dropdown_options?: Record<string, number>;
  event_to_status_map?: EventStatusMap;
  terminal_status_values?: string[];
  paused_status_values?: string[];
  suppression_field_id?: number | null;
  suppression_dropdown_option_id?: number | null;
  suppression_reason_field_id?: number | null;
  dietary_field_id?: number | null;
  comments_field_id?: number | null;
  scope_field_id?: number | null;
  scope_field_name?: string | null;
  scope_dropdown_options?: Record<string, number>;
  response_url_template?: string | null;
}

// Where a list config's recipients + statuses come from. 'affinity' is
// every list today; 'crm' rows are served by the native CRM list named
// by crm_list_id and filter by field KEY + label values (v2 shape).
export type ListSourceKind = 'affinity' | 'crm';

export interface AffinityListConfig {
  affinity_list_id: number;
  label: string | null;
  list_slug: string;
  status_field_id: number;
  status_dropdown_options: Record<string, number>;
  event_to_status_map: EventStatusMap;
  terminal_status_values: string[];
  paused_status_values: string[];
  suppression_field_id: number | null;
  suppression_dropdown_option_id: number | null;
  suppression_reason_field_id: number | null;
  dietary_field_id: number | null;
  comments_field_id: number | null;
  scope_field_id: number | null;
  scope_field_name: string | null;
  scope_dropdown_options: Record<string, number>;
  dropdown_options_refreshed_at: string | null;
  response_url_template: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Native-list source seam. Populated for every config; the crm_*
  // fields are null on affinity-sourced rows.
  source_kind: ListSourceKind;
  crm_list_id: number | null;
  crm_status_field_key: string | null;
  crm_scope_field_key: string | null;
  crm_dietary_field_key: string | null;
  crm_comments_field_key: string | null;
}

// Canonical campaign filter shape — symmetric across the two slots, and
// discriminated by source:
//
//  * affinity-sourced (v1): `field_id` is denormalized so a list-config
//    rename doesn't silently re-target a campaign (the enroller raises
//    409 on mismatch), and `values` are option IDs (stable across
//    Affinity label renames); the UI looks up labels via the list
//    config's *_dropdown_options cache.
//  * crm-sourced (v2): `field_key` names the CRM field def and `values`
//    are the option LABELS themselves — the native options cache stores
//    `{label: ordinal}` placeholders, so only its KEYS are meaningful
//    (backend: recipient_enroller._validate_native_filter_alignment).
export interface CampaignFilterSlotAffinity {
  field_id: number;
  values: number[];
}

export interface CampaignFilterSlotNative {
  field_key: string;
  values: string[];
}

export type CampaignFilterSlot =
  | CampaignFilterSlotAffinity
  | CampaignFilterSlotNative;

export interface CampaignFilter {
  status?: CampaignFilterSlot;
  scope?: CampaignFilterSlot;
}

export interface AffinityDropdownOption {
  id: number;
  text: string;
  rank: number | null;
  color: number | null;
}

// List-scoped custom merge fields (Feature 2). Admin declares a
// Jinja-safe variable name + Affinity field id; the recipient
// enroller pulls each field's value per recipient into the campaign's
// merge_fields_json, and templates reference {{variable_name}}.
export type CustomFieldValueType = 'text' | 'number' | 'dropdown' | 'date';

export interface AffinityListCustomField {
  id: number;
  affinity_list_id: number;
  variable_name: string;
  affinity_field_id: number;
  affinity_field_name: string;
  value_type: CustomFieldValueType;
  fallback_text: string;
  created_at: string;
  updated_at: string;
}

export interface AffinityListCustomFieldIn {
  variable_name: string;
  affinity_field_id: number;
  value_type?: CustomFieldValueType;
  fallback_text?: string;
}

export interface AffinityListCustomFieldUpdate {
  variable_name?: string;
  affinity_field_id?: number;
  value_type?: CustomFieldValueType;
  fallback_text?: string;
}

export interface RefreshMergeFieldsResult {
  campaign_id: number;
  updated: number;
  unchanged: number;
  missing_in_affinity: number;
  skipped_terminal: number;
  needs_data_cleared: number;
  message: string;
}

export interface AffinityListField {
  id: number;
  name: string;
  value_type: number;
  allows_multiple: boolean;
  dropdown_options: AffinityDropdownOption[];
  // Set on the list's OWN fields; null on the global person/org fields
  // Affinity V1 mixes into the same response. A list-entry write only accepts
  // a list-scoped field, so the form-session picker filters on this.
  list_id: number | null;
}

// Canonical list of campaign events that admins map to status labels.
// Matches the spec §3.9 event names. The order reflects the lifecycle
// flow as it appears in the admin UI editor.
export const CAMPAIGN_EVENTS = [
  'step_1_sent',
  'step_n_sent',
  'replied',
  'rsvp_attending',
  'rsvp_not_attending',
  'rsvp_virtual',
  'bounced',
  'unsubscribed',
] as const;

export type CampaignEvent = (typeof CAMPAIGN_EVENTS)[number];

export const CAMPAIGN_EVENT_LABELS: Record<CampaignEvent, string> = {
  step_1_sent: 'Step 1 sent',
  step_n_sent: 'Subsequent step sent',
  replied: 'Email reply received',
  rsvp_attending: 'RSVP form: attending',
  rsvp_not_attending: 'RSVP form: not attending',
  rsvp_virtual: 'RSVP virtual (manual only)',
  bounced: 'Hard bounce / NDR',
  unsubscribed: 'One-click unsubscribe',
};

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'finished';
export type SendMode = 'per_person' | 'per_organization';

export interface CampaignStepIn {
  step_order: number;
  // delay_days is deprecated in favor of delay_minutes for sub-day
  // step cadences. Both are sent on writes for backwards-compat
  // during the Phase 1 transition; the backend prefers delay_minutes
  // when non-zero. Optional on read for older API responses.
  delay_days: number;
  delay_minutes?: number;
  subject_template: string;
  body_html_template: string;
  body_text_template?: string | null;
  send_condition: 'always' | 'if_no_reply';
  include_prior_thread: boolean;
  // Step 2+: when false (default), reply on Step 1's thread (subject
  // auto-derives, In-Reply-To header set). When true, send as a new
  // email — operator's subject used verbatim, no In-Reply-To. Step 1
  // ignores the flag (no thread to reply to).
  start_new_thread: boolean;
}

export interface CampaignStep extends CampaignStepIn {
  id: number;
  campaign_id: number;
}

// Variant of CampaignStepIn used for POST /steps. Carries the
// create-only `reactivate_finished_recipients` flag — PATCH uses the
// base CampaignStepIn so the flag can't be re-triggered on an edit.
export interface CampaignStepCreateIn extends CampaignStepIn {
  reactivate_finished_recipients?: boolean;
}

// POST /steps response. Extends CampaignStep with counters from the
// reactivation side effect (zero when the flag was off).
export interface CampaignStepCreateOut extends CampaignStep {
  reactivated_count: number;
  reactivated_skipped_no_prior_send: number;
  sends_blocked_by_campaign_status: boolean;
  earliest_next_send_at: string | null;
  latest_next_send_at: string | null;
}

// GET /steps/finished-recipient-impact response. Read-only preview of
// how many finished recipients would be re-activated by a hypothetical
// new step at (step_order, delay_minutes).
export interface FinishedRecipientImpact {
  step_order: number;
  delay_minutes: number;
  eligible: number;
  skipped_no_prior_send: number;
  earliest_next_send_at: string | null;
  latest_next_send_at: string | null;
  sends_blocked_by_campaign_status: boolean;
}

// Resume-finished bulk action (Recipients tab): pick up finished recipients
// into the next step that already exists beyond their current_step.
export interface ResumeFinishedImpact {
  eligible: number;
  skipped_no_prior_send: number;
  skipped_no_next_step: number;
  earliest_next_send_at: string | null;
  latest_next_send_at: string | null;
  sends_blocked_by_campaign_status: boolean;
}

export interface ResumeFinishedResult {
  reactivated_count: number;
  skipped_no_prior_send: number;
  skipped_no_next_step: number;
  earliest_next_send_at: string | null;
  latest_next_send_at: string | null;
  sends_blocked_by_campaign_status: boolean;
}

export interface CampaignIn {
  name: string;
  sender_email: string;
  affinity_list_id: number;
  affinity_filter_json?: string | null;
  rsvp_only_filter_json?: string | null;
  send_mode: SendMode;
  schedule_json: string;
  min_seconds_between_sends_per_inbox: number;
  cc_override?: string | null;
  bcc_override?: string | null;
  // Single @ember-infra.com address; sets the Reply-To header on every
  // send. null/blank = replies go to the sender. Replies to a non-sender
  // mailbox may not be auto-recognized (stop-on-reply may not fire).
  reply_to_email?: string | null;
  track_clicks: boolean;
  track_opens: boolean;
  test_mode_enabled?: boolean;
  // The form this campaign uses for its RSVP/response page.
  // Optional on create (drafts can fill in later) but required by
  // /start (server returns 409 if NULL when starting).
  form_id?: number | null;
  steps: CampaignStepIn[];
}

export interface CampaignUpdate {
  name?: string;
  sender_email?: string;
  affinity_list_id?: number;
  affinity_filter_json?: string | null;
  rsvp_only_filter_json?: string | null;
  send_mode?: SendMode;
  schedule_json?: string;
  min_seconds_between_sends_per_inbox?: number;
  cc_override?: string | null;
  bcc_override?: string | null;
  reply_to_email?: string | null;
  track_clicks?: boolean;
  track_opens?: boolean;
  test_mode_enabled?: boolean;
  form_id?: number | null;
  calendar_invites_enabled?: boolean;
}

export interface Campaign {
  id: number;
  name: string;
  sender_email: string;
  affinity_list_id: number;
  affinity_filter_json: string | null;
  rsvp_only_filter_json: string | null;
  send_mode: SendMode;
  schedule_json: string;
  min_seconds_between_sends_per_inbox: number;
  cc_override: string | null;
  bcc_override: string | null;
  reply_to_email: string | null;
  track_clicks: boolean;
  track_opens: boolean;
  test_mode_enabled: boolean;
  status: CampaignStatus;
  // Set when the campaign is archived (declutter the campaigns list).
  // Orthogonal to status -- archive is purely a UI grouping signal,
  // doesn't affect sequencer / Affinity behavior.
  archived_at: string | null;
  // Linked form (drives RSVP page + response write-back targets).
  // NULL on legacy drafts; required before /start.
  form_id: number | null;
  // Calendar event metadata. NULL when the campaign has no associated
  // event. The RSVP approval queue's send_invite action requires
  // starts_at + ends_at to be populated; PATCH /campaigns/{id}/event
  // sets these.
  event_starts_at: string | null;
  event_ends_at: string | null;
  // Calendar invite subject. Falls back to `name` server-side when null.
  event_subject: string | null;
  event_location: string | null;
  event_body_html: string | null;
  event_online_meeting: boolean;
  event_timezone: string | null;
  // Per-campaign toggle for "Send calendar invites" on the RSVP
  // queue's Approve action. When false, the queue still populates
  // from form RSVPs but Approve refuses to send a calendar invite
  // even if event details are configured. Duplicate sets this false
  // by default; the admin must flip it on after reviewing the clone.
  calendar_invites_enabled: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  steps: CampaignStep[];
  // Engagement counts populated by the backend (one batched aggregate
  // query per list call). Recipient_count is the enrollment count;
  // clicked/replied are distinct-recipient counts; rsvp_yes_count
  // counts recipients whose Affinity status maps to the campaign's
  // rsvp_attending OR rsvp_virtual event labels.
  recipient_count: number;
  // Distinct recipients with at least one campaign_sends row in a
  // sent send_status (excludes test sends). Surfaced as "Sent" between
  // Recipients (enrolled) and Clicked on the Campaigns list.
  sent_count: number;
  clicked_count: number;
  opened_count: number;
  replied_count: number;
  rsvp_yes_count: number;
  // Pending (operator-action-required) RSVP approval rows on this
  // campaign. Powers the Campaigns-tab Queue badge.
  pending_rsvp_count: number;
}

export interface CampaignEventConfig {
  event_starts_at?: string | null;
  event_ends_at?: string | null;
  event_subject?: string | null;
  event_location?: string | null;
  event_body_html?: string | null;
  event_online_meeting?: boolean;
  event_timezone?: string | null;
}

export interface EventTestSendResult {
  sent_to: string;
  subject: string;
  graph_event_id: string;
  message: string;
}

// ---------------------------------------------------------------------------
// RSVP Approval Queue
// ---------------------------------------------------------------------------

export type RsvpTriggerSource = 'form' | 'reply' | 'affinity_poll' | 'manual';
export type RsvpClassifierVerdict = 'yes' | 'no' | 'maybe' | 'unrelated';
export type RsvpClassifierConfidence = 'high' | 'medium' | 'low';
export type RsvpApprovalStatus = 'pending' | 'sent' | 'send_error' | 'dismissed';
export type RsvpManualAction = 'yes' | 'no' | 'just_invite';

export interface RsvpApprovalRow {
  id: number;
  campaign_id: number;
  recipient_id: number;
  recipient_email: string;
  recipient_first_name: string | null;
  recipient_last_name: string | null;
  recipient_company: string | null;
  enrollment_kind: EnrollmentKind;
  trigger_source: RsvpTriggerSource;
  classifier_verdict: RsvpClassifierVerdict | null;
  classifier_confidence: RsvpClassifierConfidence | null;
  classifier_excerpt: string | null;
  classifier_reasoning: string | null;
  proposed_status_event: string | null;
  proposed_send_invite: boolean;
  status: RsvpApprovalStatus;
  created_at: string;
  approved_at: string | null;
  approved_by: string | null;
  sent_at: string | null;
  graph_event_id: string | null;
  affinity_status_written: string | null;
  error_text: string | null;
  has_prior_sent_invite: boolean;
}

export interface RsvpApprovalsResponse {
  rows: RsvpApprovalRow[];
  total: number;
  by_status: Partial<Record<RsvpApprovalStatus, number>>;
}

export interface RsvpApprovalActionResult {
  id: number;
  status: RsvpApprovalStatus;
  affinity_status_written?: string | null;
  graph_event_id?: string | null;
  error_step?: string | null;
  error_text?: string | null;
}

export interface RsvpBulkApproveResultItem {
  id: number;
  // 'sent' / 'send_error' from the row state, or 'error' for rows that
  // never made it to execute (not found, maybe/null-verdict rejected,
  // missing event metadata).
  status: 'sent' | 'send_error' | 'error';
  error_step?: string | null;
  error_text?: string | null;
  affinity_status_written?: string | null;
  graph_event_id?: string | null;
}

export interface RsvpBulkApproveResult {
  succeeded: number;
  failed: number;
  results: RsvpBulkApproveResultItem[];
}

export interface CampaignActionResult {
  campaign_id: number;
  status: string;
  message?: string | null;
  enrolled?: number | null;
  excluded_paused?: number | null;
  excluded_suppressed?: number | null;
  skipped_no_email?: number | null;
  skipped_already_enrolled?: number | null;
  enrolled_rsvp_only?: number | null;
  skipped_already_enrolled_rsvp_only?: number | null;
  promoted_rsvp_only_to_campaign?: number | null;
}

// Reactivate-specific request + result. Mirrors
// backend/app/schemas_outreach.py::ReactivateCampaignRequest /
// ::ReactivateCampaignResult. Defaults match DQ-7's recommended
// posture (resume replied-elsewhere on, manual off, re-enroll off).
export interface ReactivateCampaignRequest {
  re_enroll: boolean;
  resume_replied_elsewhere: boolean;
  resume_paused_manual: boolean;
}

export interface ReactivateCampaignResult extends CampaignActionResult {
  re_enrolled_count: number | null;
  resumed_recipients: Record<string, number>;
  no_active_recipients_after: boolean;
}

// ---------------------------------------------------------------------------
// Recipients
// ---------------------------------------------------------------------------

export type RecipientStatus =
  | 'pending'
  | 'throttled'
  | 'in_progress'
  | 'replied'
  | 'bounced'
  | 'unsubscribed'
  | 'suppressed'
  | 'paused_manual'
  | 'paused_replied_elsewhere'
  | 'finished'
  | 'errored'
  | 'removed';

export type EnrollmentKind = 'campaign' | 'rsvp_only';

export interface CampaignRecipient {
  id: number;
  campaign_id: number;
  // Dual-keyed identity (native-lists migration S1): affinity-sourced
  // rows carry the affinity_* ids; crm-sourced rows carry crm_* ids
  // and the affinity ids are null. At least one person id is present.
  affinity_person_id: number | null;
  affinity_org_id: number | null;
  affinity_list_entry_id: number | null;
  crm_person_id: number | null;
  crm_org_id: number | null;
  crm_list_entry_id: number | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  status: RecipientStatus;
  enrollment_kind: EnrollmentKind;
  // Index of the LAST completed step. The next step the sequencer
  // will attempt is `current_step + 1`. UI labels this as "Next step".
  current_step: number;
  // Most recent live (non-test) send timestamp; null until step 1 has
  // gone out. Powers the "Last sent" column.
  last_sent_at: string | null;
  next_send_at: string | null;
  replied_at: string | null;
  // 'yes' = latest RSVP via form/link is rsvp_attending
  // 'no'  = latest RSVP is rsvp_not_attending
  // null  = no form/link RSVP recorded; if status='replied' it means
  //         the recipient replied via email rather than the RSVP form
  rsvp_response: 'yes' | 'no' | null;
  bounced_at: string | null;
  unsubscribed_at: string | null;
  current_list_status: string | null;
  // Engagement sums across this recipient's live sends. open_count is
  // meaningful only when the campaign enabled track_opens; click_count
  // only when track_clicks was on. The table renders "—" for an untracked
  // campaign rather than 0.
  open_count: number;
  click_count: number;
}

export interface CampaignRecipientsResponse {
  total: number;
  by_status: Partial<Record<RecipientStatus, number>>;
  by_enrollment_kind: Partial<Record<EnrollmentKind, number>>;
  rows: CampaignRecipient[];
}

export interface CampaignStepStats {
  step_order: number;
  sent: number;
  clicked: number;
  opened: number;
  bounced: number;
}

export interface CampaignTestCounts {
  total_sends: number;
  total_clicks: number;
  total_hard_bounces: number;
  by_step: CampaignStepStats[];
}

export interface CampaignStats {
  total_recipients: number;
  by_status: Partial<Record<RecipientStatus, number>>;
  total_sends: number;
  total_replies: number;
  total_unmatched_replies: number;
  total_clicks: number;
  total_opens: number;
  total_hard_bounces: number;
  by_step: CampaignStepStats[];
  // Populated only when ?include_test=true; null/undefined otherwise.
  test?: CampaignTestCounts | null;
}

export interface UnmatchedReply {
  id: number;
  internet_message_id: string | null;
  in_reply_to: string | null;
  from_email: string;
  received_at: string;
  body_excerpt: string | null;
  source: string;
}

// ---------------------------------------------------------------------------
// Settings (singleton; global test-mode toggle)
// ---------------------------------------------------------------------------

export interface OutreachSettings {
  test_mode_enabled: boolean;
  test_recipient: string;
  updated_by: string | null;
  updated_at: string;
}

export interface OutreachSettingsUpdate {
  test_mode_enabled?: boolean;
  test_recipient?: string;
}

// ---------------------------------------------------------------------------
// Recipient bulk actions + test-send
// ---------------------------------------------------------------------------

export interface RecipientBulkActionResult {
  affected: number;
  skipped: number;
  message: string | null;
}

export interface RecipientHardDeleteRefusal {
  recipient_id: number;
  reason: 'has_reply' | 'has_in_flight_send';
}

export interface RecipientBulkHardDeleteResult {
  deleted: number;
  refused: RecipientHardDeleteRefusal[];
  message: string | null;
}

export interface TestSendRequest {
  recipient_id: number;
  test_email: string;
  step_order?: number;
}

export interface TestSendResult {
  sent_to: string;
  subject: string;
  rendered_for_recipient_id: number;
  step_order: number;
  internet_message_id: string | null;
  message: string;
}

// ---------------------------------------------------------------------------
// Status / monitoring
// ---------------------------------------------------------------------------

export type CampaignTickEventType =
  | 'sent'
  | 'throttled'
  | 'out_of_window'
  | 'errored'
  | 'finished_no_more_steps';

export interface CampaignTickEvent {
  id: number;
  campaign_id: number;
  recipient_id: number | null;
  recipient_email: string | null;
  step_id: number | null;
  step_order: number | null;
  event_type: CampaignTickEventType;
  detail: string | null;
  is_test: boolean;
  occurred_at: string;
}

export type TimelineEventKind =
  | 'sent' | 'opened' | 'replied' | 'bounced' | 'clicked' | 'rsvp' | 'tick_event';

export interface TimelineEvent {
  occurred_at: string;
  kind: TimelineEventKind;
  summary: string;
  detail?: string | null;
  is_test: boolean;
  internet_message_id?: string | null;
  event_type?: string | null;
  step_order?: number | null;
}

export interface CampaignErrorRow {
  tick_event_id: number;
  occurred_at: string;
  recipient_id: number | null;
  recipient_email: string | null;
  step_order: number | null;
  detail: string;
  is_test: boolean;
  recipient_status: RecipientStatus | null;
}

export interface RecipientRetryResult {
  recipient_id: number;
  previous_status: string;
  new_status: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Step deletion preview + outreach assets
// ---------------------------------------------------------------------------

export interface StepDeletionImpact {
  step_id: number;
  step_order: number;
  in_flight_recipients_skipping: number;
  already_passed: number;
  total_sends_for_step: number;
}

export interface OutreachAsset {
  filename: string;
  url: string;
  size_bytes: number;
  uploaded_at: string;
}

export interface OutreachAssetUploadResult {
  asset: OutreachAsset;
  message: string;
}

// ---------------------------------------------------------------------------
// Campaign audit log (Activity tab)
// ---------------------------------------------------------------------------

// Closed-set vocabulary; mirror of backend
// services/outreach/audit.py::ACTIONS. Adding a new key here means
// adding it server-side too — otherwise the Activity tab shows an
// "unknown action" placeholder.
export const CAMPAIGN_AUDIT_ACTIONS = [
  'created',
  'updated',
  'enrolled',
  'started',
  'paused',
  'resumed',
  'finished',
  'deleted',
  'step_added',
  'step_added_with_reactivation',
  'resumed_finished_recipients',
  'step_updated',
  'step_deleted',
  'test_send',
  'recipient_removed',
  'recipient_restored',
  'recipient_marked_bounced',
  'recipients_cleared',
  'recipients_hard_deleted',
  'asset_uploaded',
  'asset_deleted',
  'archived',
  'unarchived',
  'schedule_reset_next_send_at',
] as const;

export type CampaignAuditAction = typeof CAMPAIGN_AUDIT_ACTIONS[number];

export const CAMPAIGN_AUDIT_LABELS: Record<CampaignAuditAction, string> = {
  created:            'Created campaign',
  updated:            'Updated campaign',
  enrolled:           'Enrolled recipients',
  started:            'Started',
  paused:             'Paused',
  resumed:            'Resumed',
  finished:           'Finished',
  deleted:            'Deleted',
  step_added:         'Added step',
  step_added_with_reactivation: 'Added step + re-activated finished recipients',
  resumed_finished_recipients: 'Resumed finished recipients into next step',
  step_updated:       'Updated step',
  step_deleted:       'Deleted step',
  test_send:          'Test send',
  recipient_removed:  'Removed recipients',
  recipient_restored: 'Restored recipients',
  recipient_marked_bounced: 'Marked recipient as bounced (manual)',
  recipients_cleared: 'Cleared all recipients',
  recipients_hard_deleted: 'Hard-deleted recipients (subset)',
  asset_uploaded:     'Uploaded asset',
  asset_deleted:      'Deleted asset',
  archived:           'Archived',
  unarchived:         'Unarchived',
  schedule_reset_next_send_at: 'Reset send schedule (recipients re-evaluated)',
};

export interface CampaignAuditEntry {
  id: number;
  campaign_id: number | null;
  actor_email: string;
  action: string;            // intentionally `string` not the union — backend may have unknown keys
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

export interface CampaignAuditResponse {
  rows: CampaignAuditEntry[];
  total: number;
}


// ---------------------------------------------------------------------------
// Form responses (top-level Outreach > Forms tab)
// ---------------------------------------------------------------------------

export interface FormResponse {
  id: number;
  submitted_at: string;
  recipient_id: number;
  recipient_email: string;
  recipient_name: string | null;
  recipient_company: string | null;
  campaign_id: number;
  campaign_name: string;
  // Raw response_type from the backend (rsvp_attending /
  // rsvp_not_attending / rsvp_virtual / custom).
  response_type: string;
  // Compact 'yes' / 'no' / null. Null when response_type is something
  // other than the attending pair, in which case the UI falls back to
  // showing response_type directly.
  attending: 'yes' | 'no' | null;
  dietary: string | null;
  comments: string | null;
  // Per-session answers ({session_key: 'yes'|'no'}) when the form carried
  // sessions. Null on legacy rows and on forms without them — the Responses
  // table only grows chip columns when at least one row has them.
  sessions: Record<string, 'yes' | 'no'> | null;
  submitted_email: string | null;
  user_agent: string | null;
}

export interface FormResponsesResponse {
  rows: FormResponse[];
  // Unfiltered total -- the UI uses this with `rows.length` to render
  // "showing X of Y" without re-running the filter client-side.
  total: number;
}


// ---------------------------------------------------------------------------
// Forms (top-level entities — defined separately from list configs)
// ---------------------------------------------------------------------------

// One Yes/No row on a form (e.g. "Welcome Reception"). Array order IS the
// render order — the server writes `position` from the index.
export interface FormSessionIn {
  session_key: string;
  label: string;
  when_text?: string | null;
  where_text?: string | null;
  // Exactly one row per form. Its answer becomes the form's overall
  // `attending`, so it drives the list Status field and the invite queue.
  is_primary: boolean;
  affinity_field_id?: number | null;
  yes_option_label: string;
  no_option_label: string;
  // {option label: option id} cache, filled by the field picker on select.
  dropdown_options: Record<string, number>;
  crm_field_key?: string | null;
}

export interface FormSession extends FormSessionIn {
  id: number;
  position: number;
}


export interface FormIn {
  slug: string;
  name: string;
  headline: string;
  dietary_enabled?: boolean;
  comments_enabled?: boolean;
  // Affinity list IDs the form's submissions write back to. M:N --
  // a form can write to 0, 1, or many lists.
  list_ids?: number[];
  // Ordered Yes/No rows. Omitted / empty = the single "Will you attend?"
  // question the form has always rendered.
  sessions?: FormSessionIn[];
  // Per-form customization. Empty string clears back to default.
  contact_email?: string | null;
  not_on_list_message?: string | null;
  blocked_message?: string | null;
}

export interface FormUpdate {
  slug?: string;
  name?: string;
  headline?: string;
  dietary_enabled?: boolean;
  comments_enabled?: boolean;
  // Replaces the current set when provided; pass [] to clear.
  list_ids?: number[];
  // Same contract as list_ids: omitted leaves the rows alone, a list replaces
  // them wholesale, [] clears them.
  sessions?: FormSessionIn[];
  contact_email?: string | null;
  not_on_list_message?: string | null;
  blocked_message?: string | null;
}

export interface OutreachForm {
  id: number;
  slug: string;
  name: string;
  headline: string;
  dietary_enabled: boolean;
  comments_enabled: boolean;
  list_ids: number[];
  sessions: FormSession[];
  // Per-form customization. NULL/empty means "fall back to the canned
  // text with agm@ember-infra.com".
  contact_email: string | null;
  not_on_list_message: string | null;
  blocked_message: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}


// Reusable step subject + body templates. send_mode_compat is auto-
// detected at save time from the Jinja variables used.
export type StepTemplateCompat = 'per_person' | 'per_organization' | 'both';

export interface StepTemplate {
  id: number;
  name: string;
  subject_template: string | null;
  body_html_template: string;
  send_mode_compat: StepTemplateCompat;
  variables_used: string[];
  notes: string | null;
  created_by_email: string;
  updated_by_email: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface StepTemplateIn {
  name: string;
  subject_template?: string | null;
  body_html_template: string;
  notes?: string | null;
}

export interface StepTemplateUpdate {
  name?: string;
  subject_template?: string | null;
  body_html_template?: string;
  notes?: string | null;
  archived?: boolean;
}
